import { httpBlob, httpRequest } from './http.js'
import type { AssetPresenceEntry } from '../types/index.js'
import type { LiveDeinterlace } from './liveDeinterlace.js'

async function apiFetch<T>(path: string, opts: RequestInit = {}): Promise<T> {
  return httpRequest<T>(path, opts)
}
const get    = <T>(p: string)              => apiFetch<T>(p)
const post   = <T>(p: string, b?: unknown) => apiFetch<T>(p, { method: 'POST',   body: b ? JSON.stringify(b) : undefined })
const put    = <T>(p: string, b: unknown)  => apiFetch<T>(p, { method: 'PUT',    body: JSON.stringify(b) })
const patch  = <T>(p: string, b: unknown)  => apiFetch<T>(p, { method: 'PATCH',  body: JSON.stringify(b) })
const del    = <T>(p: string)              => apiFetch<T>(p, { method: 'DELETE' })
const qs     = (p: Record<string, unknown>) => { const e = Object.entries(p).filter(([,v]) => v !== undefined && v !== ''); return e.length ? '?' + e.map(([k,v]) => `${k}=${encodeURIComponent(String(v))}`).join('&') : '' }

// ─── Live Sources ─────────────────────────────────────────────────────────────

export interface LiveSourceConfig {
  id:                   string
  channelId:            string
  name:                 string
  sourceType:           'DECKLINK' | 'INTERNET' | 'RTMP' | 'RTSP' | 'SRT'
  connectionString:     string | null
  channelIndex:         number | null
  /** Format SDI forcé (ex. « 1080p5000 »). Null = mode du canal. */
  sdiFormat:            string | null
  /** Désentrelacement d'une source Internet ; null = Auto. Jamais pour une DeckLink. */
  deinterlace?:         LiveDeinterlace | null
  status:               string
  reconnectAttempts:    number
  reconnectIntervalSec: number
  lastSeen:             string | null
  active:               boolean
}

/**
 * Formats SDI proposés pour une entrée DeckLink. Miroir de
 * `backend/src/lib/caspar-decklink-source.ts` : liste fermée, parce qu'une
 * faute de frappe redonnerait la panne silencieuse (image noire, aucune
 * erreur) que ce champ sert à éviter.
 */
export const DECKLINK_SDI_FORMATS = [
  '1080p5000', '1080i5000', '1080p2500',
  '1080p5994', '1080i5994', '1080p2997',
  '1080p6000', '1080i6000', '1080p3000',
  '1080p2398', '1080p2400',
  '720p5000', '720p5994', '720p6000',
  'PAL', 'NTSC',
] as const

export const liveSourcesApi = {
  list:   (channelId: string)                => get<LiveSourceConfig[]>(`/live-sources${qs({ channelId })}`),
  create: (data: Partial<LiveSourceConfig>)  => post<LiveSourceConfig>('/live-sources', data),
  update: (id: string, d: Partial<LiveSourceConfig>) => patch<LiveSourceConfig>(`/live-sources/${id}`, d),
  delete: (id: string)                       => del<void>(`/live-sources/${id}`),
  /** `deinterlace` : réglage à essayer sur le canal de test, sans l'enregistrer. */
  test:   (id: string, opts?: { previewChannel?: number; stop?: boolean; deinterlace?: LiveDeinterlace }) =>
    post<{ ok: boolean; message: string; previewing?: boolean; stopped?: boolean }>(`/live-sources/${id}/test`, opts ?? {}),
}

// ─── Canal de test CasparCG (CH2) ─────────────────────────────────────────────

/** Canal CasparCG de test : jamais l'antenne. Miroir de `CG_TEST_CASPAR_CHANNEL`. */
export const TEST_CASPAR_CHANNEL = 2

export interface TestChannelSlot {
  /** `VIDEO` (source live, couche 1) ou `CG:<couche>-<layer CG>`. */
  slot:      string
  kind:      'SOURCE' | 'CG' | string
  refId:     string
  label:     string
  detail:    string | null
  /** Réglages envoyés avec la source (`{ deinterlace }`). */
  settings?: { deinterlace?: LiveDeinterlace } | null
  sentAt:    string
  actorName: string | null
}

export interface TestChannelEvent {
  id:        string
  kind:      'CG_SENT' | 'CG_REMOVED' | 'SOURCE_SENT' | 'SOURCE_STOPPED' | 'SOURCE_REFUSED' | 'SOURCE_SETTINGS_SAVED' | 'SOURCE_RECONNECTING' | 'SOURCE_RECOVERED' | 'SOURCE_RECONNECT_ABANDONED' | 'CHANNEL_CLEARED' | 'CHANNEL_RESET' | string
  label:     string
  detail:    string | null
  actorName: string | null
  createdAt: string
}

export interface TestChannelState {
  casparChannel: number
  /** Faux quand CasparCG n'a pas répondu : l'état est celui de la base. */
  observed:      boolean
  slots:         TestChannelSlot[]
  events:        TestChannelEvent[]
}

export const testChannelApi = {
  state: (channelId: string) => get<TestChannelState>(`/test-channel/${channelId}`),
  clear: (channelId: string) => post<TestChannelState>(`/test-channel/${channelId}/clear`),
}

// ─── Interlude Clips ──────────────────────────────────────────────────────────

export interface InterludeClipConfig {
  id:          string
  channelId:   string
  name:        string
  filePath:    string | null
  durationSec: number | null
  clipType:    'LOOP' | 'PROMO' | 'COMING_NEXT' | 'SLATE' | 'EMERGENCY'
  priority:    number
  active:      boolean
  tags:        string[]
  /** Présence du fichier sur disque. `null` = non suivi (chemin vide ou flux). */
  presence?:   AssetPresenceEntry | null
}

export const interludeApi = {
  list:   (channelId: string)                   => get<InterludeClipConfig[]>(`/interlude-clips${qs({ channelId })}`),
  create: (data: Partial<InterludeClipConfig>)  => post<InterludeClipConfig>('/interlude-clips', data),
  update: (id: string, d: Partial<InterludeClipConfig>) => patch<InterludeClipConfig>(`/interlude-clips/${id}`, d),
  delete: (id: string)                          => del<void>(`/interlude-clips/${id}`),
}

// ─── Auto-interlude toggle ────────────────────────────────────────────────────

export const autoInterludeApi = {
  get: (channelId: string) =>
    get<{ enabled: boolean }>(`/playout/${channelId}/auto-interlude`),
  set: (channelId: string, enabled: boolean) =>
    apiFetch<{ enabled: boolean; message: string }>(`/playout/${channelId}/auto-interlude`, {
      method: 'POST',
      body:   JSON.stringify({ enabled }),
    }),
}

// ─── Upload fichier interlude ─────────────────────────────────────────────────

export interface InterludeUploadResult {
  fileName:     string
  filePath:     string   // Chemin CasparCG relatif : "interludes/nom"
  codec:        string | null
  durationSec:  number | null
  fileSizeBytes: string | null
  status:       string
}

export const uploadInterludeApi = {
  upload: async (file: File): Promise<InterludeUploadResult> => {
    const form = new FormData()
    form.append('file', file)
    return httpRequest<InterludeUploadResult>('/upload-interlude', { method: 'POST', body: form })
  },
}

// ─── CG Templates ────────────────────────────────────────────────────────────

export interface CgTemplateConfig {
  id:           string
  channelId:    string
  name:         string
  templateType: string
  htmlPath:     string
  defaultData:  Record<string, unknown> | null
  active:       boolean
}

export const cgTemplatesApi = {
  list:   (channelId: string)                      => get<CgTemplateConfig[]>(`/cg-templates${qs({ channelId })}`),
  create: (data: Partial<CgTemplateConfig>)        => post<CgTemplateConfig>('/cg-templates', data),
  update: (id: string, d: Partial<CgTemplateConfig>) => patch<CgTemplateConfig>(`/cg-templates/${id}`, d),
  delete: (id: string)                             => del<void>(`/cg-templates/${id}`),
}

// ─── Logos antenne ────────────────────────────────────────────────────────────

export interface LogoInfo {
  bugType:  string
  fileName: string
  url:      string   // chemin absolu serveur, ex: /logos/lmtv_sarthe.png
  exists:   boolean
}

export const logosApi = {
  list: () => get<LogoInfo[]>('/logos'),

  upload: (bugType: string, file: File): Promise<{ fileName: string; url: string }> => {
    const form = new FormData()
    form.append('file', file)
    return httpRequest<{ fileName: string; url: string }>(`/logos/${bugType}`, { method: 'POST', body: form })
  },
}

// ─── Watchdog config par chaîne ──────────────────────────────────────────────

export interface WatchdogConfig {
  tickIntervalMs:     number
  freezeThresholdSec: number
  preloadAlertSec:    number
  gapFillMinSec:      number
}

export const DEFAULT_WATCHDOG_CONFIG: WatchdogConfig = {
  tickIntervalMs:     500,
  freezeThresholdSec: 6,
  preloadAlertSec:    60,
  gapFillMinSec:      2,
}

export const watchdogConfigApi = {
  get:    (channelId: string) => get<WatchdogConfig>(`/settings/watchdog/${channelId}`),
  update: (channelId: string, config: WatchdogConfig) =>
    patch<WatchdogConfig>(`/settings/watchdog/${channelId}`, config),
}

// ─── Jingle de transition avant infoservice (fin manuelle de direct) ─────────

export interface ManualEndJingleConfig {
  manualEndJingleProgramId: string | null
  program: { id: string; title: string; durationSec: number; active: boolean } | null
}

export const manualEndJingleApi = {
  get:    (channelId: string) => get<ManualEndJingleConfig>(`/settings/manual-end-jingle/${channelId}`),
  update: (channelId: string, programId: string | null) =>
    patch<ManualEndJingleConfig>(`/settings/manual-end-jingle/${channelId}`, { programId }),
}

// ─── Jingle de retour au direct après un décrochage ─────────────────────────

export interface LiveResumeJingleConfig {
  liveResumeJingleProgramId: string | null
  program: { id: string; title: string; durationSec: number; active: boolean } | null
}

export const liveResumeJingleApi = {
  get:    (channelId: string) => get<LiveResumeJingleConfig>(`/settings/live-resume-jingle/${channelId}`),
  update: (channelId: string, programId: string | null) =>
    patch<LiveResumeJingleConfig>(`/settings/live-resume-jingle/${channelId}`, { programId }),
}

// ─── Jingles de liaison (recalage, dépôt de modèle) ─────────────────────────

export interface JunctionJinglesConfig {
  programIds: string[]
}

export const junctionJinglesApi = {
  get:    (channelId: string) => get<JunctionJinglesConfig>(`/settings/junction-jingles/${channelId}`),
  update: (channelId: string, programIds: string[]) =>
    patch<JunctionJinglesConfig>(`/settings/junction-jingles/${channelId}`, { programIds }),
}

// ─── Habillage des plages d'infoservice ──────────────────────────────────────

export interface InfoserviceCgConfig {
  templateId:       string | null
  offsetSec:        number
  hideBeforeEndSec: number
  template:         { id: string; name: string; active: boolean } | null
}

export interface InfoserviceCgUpdate {
  templateId:       string | null
  offsetSec:        number
  hideBeforeEndSec: number
}

export const infoserviceCgApi = {
  get:    (channelId: string) => get<InfoserviceCgConfig>(`/settings/infoservice-cg/${channelId}`),
  update: (channelId: string, config: InfoserviceCgUpdate) =>
    patch<InfoserviceCgConfig>(`/settings/infoservice-cg/${channelId}`, config),
}

// ─── Notifications d'incident ────────────────────────────────────────────────

export interface NotificationConfig {
  enabled:       boolean
  /** Niveaux notifiés : WARNING, ERROR, CRITICAL (FATAL est compris dans CRITICAL). */
  severityTiers: string[]
  /** Alertes coupées, au format `NIVEAU:TYPE`. */
  mutedTypes:    string[]
  cooldownSec:   number

  emailEnabled:    boolean
  smtpHost:        string | null
  smtpPort:        number
  smtpSecure:      boolean
  smtpUser:        string | null
  emailFrom:       string | null
  emailTo:         string[]
  hasSmtpPassword: boolean

  telegramEnabled:  boolean
  telegramChatIds:  string[]
  hasTelegramToken: boolean

  programEnabled:      boolean
  programEvents:       string[]
  programChatIds:      string[]
  programEmailEnabled: boolean
  programEmailTo:      string[]
  liveLeadMinutes:     number

  /** Rapport journalier de la rédaction : préparation du lendemain. */
  reportEnabled:         boolean
  /** `HH:MM`, horloge d'antenne. */
  reportTime:            string
  /** Jours ISO d'envoi : 1 = lundi … 7 = dimanche. */
  reportDays:            number[]
  reportEmailTo:         string[]
  reportTelegramEnabled: boolean
  reportChatIds:         string[]
  /** Dernière journée d'antenne (`AAAA-MM-JJ`) pour laquelle le rapport est parti. */
  reportLastSentDay:     string | null

  /** Alerte de 18:00 sur les annonces de l'infoservice du lendemain (flux éditorial). */
  infoserviceAlertEnabled:     boolean
  infoserviceAlertLastSentDay: string | null

  /** ISO tant qu'un silence de maintenance court, `null` sinon. */
  maintenanceUntil: string | null

  updatedAt: string
}

/** Les secrets sont en écriture seule : `''` efface, `undefined` conserve. */
export interface NotificationConfigPatch extends Partial<Omit<NotificationConfig,
  'hasSmtpPassword' | 'hasTelegramToken' | 'maintenanceUntil' | 'updatedAt' | 'reportLastSentDay' | 'infoserviceAlertLastSentDay'>> {
  smtpPassword?:     string
  telegramBotToken?: string
}

export interface NotificationDelivery {
  id:        string
  channelId: string | null
  transport: 'EMAIL' | 'TELEGRAM'
  alertType: string
  severity:  string
  target:    string
  subject:   string
  status:    'SENT' | 'FAILED'
  error:     string | null
  createdAt: string
}

export interface NotificationConfigResponse {
  config:        NotificationConfig
  /** Alertes réellement émises, rangées par niveau. */
  alertCatalogue: Record<string, string[]>
  /** Niveaux, du moins grave au plus grave. */
  tiers:          string[]
  programEvents: string[]
}

export interface DailyReportPreview {
  subject:  string
  text:     string
  html:     string
  telegram: string
}

export interface TelegramChat {
  id:    string
  title: string
  type:  string
}

export const notificationsApi = {
  get:    ()                              => get<NotificationConfigResponse>('/notifications/config'),
  update: (data: NotificationConfigPatch) => put<{ config: NotificationConfig }>('/notifications/config', data),
  test:   (transport: 'EMAIL' | 'TELEGRAM', stream: 'INCIDENT' | 'PROGRAM' = 'INCIDENT') =>
    post<{ ok: true }>('/notifications/test', { transport, stream }),
  telegramChats: () => get<{ chats: TelegramChat[] }>('/notifications/telegram/chats'),
  maintenance: (minutes: number) =>
    post<{ maintenanceUntil: string | null }>('/notifications/maintenance', { minutes }),
  dailyReportPreview: () => get<{ report: DailyReportPreview }>('/notifications/daily-report/preview'),
  dailyReportSend:    () => post<{ ok: true; channels: number }>('/notifications/daily-report/send'),
  deliveries: (limit = 50) =>
    get<{ deliveries: NotificationDelivery[] }>(`/notifications/deliveries${qs({ limit })}`),
}

export const ALERT_TYPE_LABELS: Record<string, string> = {
  FREEZE_DETECTED:               'Image figée à l’antenne',
  SIGNAL_LOST:                   'Perte de signal live',
  LOAD_FAILURE:                  'Échec de chargement média',
  INTERLUDE_UNAVAILABLE:         'Boucle d’interlude introuvable',
  MEDIA_MISSING:                 'Média manquant avant diffusion',
  DURATION_DRIFT:                'Dérive de durée',
  AUDIO_SILENCE:                 'Silence audio',
  LIVE_ENDING_SOON:              'Fin de direct imminente',
  PLAYOUT_ENGINE_DISCONNECTED:   'Moteur de diffusion déconnecté',
  PLAYOUT_ENGINE_COMMAND_FAILED: 'Commande de diffusion refusée',
  PLAYOUT_FAILOVER:              'Bascule vers la diffusion de secours',
  PLAYOUT_FAILOVER_FAILED:           'Bascule de secours refusée',
  PLAYOUT_FAILOVER_PROMOTION_FAILED: 'Bascule faite, reprise de grille en échec',
  PLAYOUT_FAILOVER_DISARMED:         'Bascule automatique désarmée',
  PLAYOUT_MEDIA_PREFLIGHT_FAILED:    'Médias absents sur la cible de secours',
  EARLY_MEDIA_END:                   'Fichier plus court que sa case (interlude posé)',
  GAP_UPCOMING:                      'Trou dans la grille à venir (10, 5 et 2 min)',
  GAP_STARTED:                       'Trou à l’antenne : interlude lancé',
  LIVE_SIGNAL_SUSPECT:               'Direct instable (image figée)',
  LIVE_SIGNAL_TIME_UNAVAILABLE:      'Direct Internet sans temps média',
  LIVE_STREAM_FAILOVER:              'Direct Internet figé : interlude et reconnexion',
  LIVE_SIGNAL_LOST_SUPPRESSED:       'Perte du direct sans secours automatique',
  LIVE_FAILOVER_SUPPRESSED_NEAR_END: 'Direct suspect en fin, secours retenu',
  EPG_EXPORT_FAILED:             'Dépôt EPG en échec',
  YOUTUBE_SYNC_FAILED:           'Titre YouTube non mis à jour',
}

export const PROGRAM_EVENT_LABELS: Record<string, string> = {
  PROGRAM_START:      'Lancement d’un programme',
  PROGRAM_END:        'Fin d’un programme',
  LIVE_STARTING_SOON: 'Direct à venir (pré-annonce)',
}

/** Libellé du rapport journalier dans l'historique des envois. */
export const DAILY_REPORT_DELIVERY_LABEL = 'Rapport journalier'

/** Libellé de l'alerte de la veille sur les annonces de l'infoservice. */
export const INFOSERVICE_ALERT_DELIVERY_LABEL = 'Annonces infoservice (veille)'

/**
 * Niveaux de notification technique, tels que l'astreinte les lit. Pas de
 * niveau « fatal » séparé : le backend range FATAL dans le critique.
 */
export const SEVERITY_TIERS: Record<string, { label: string; description: string }> = {
  WARNING: {
    label:       'Avertissements',
    description: 'L’antenne continue, mais quelque chose dérive ou mérite un œil : dérive de durée, silence audio, direct instable, trou à venir, titre YouTube.',
  },
  ERROR: {
    label:       'Erreurs',
    description: 'Un défaut à corriger avant qu’il n’atteigne l’antenne : média manquant sur un élément à venir, dépôt EPG refusé.',
  },
  CRITICAL: {
    label:       'Critiques',
    description: 'L’antenne est touchée ou le secours a pris la main : trou à l’antenne, image figée, perte de signal, moteur déconnecté, bascule.',
  },
}

// ─── Export EPG (Plurimedia / Media Press) ───────────────────────────────────

export interface EpgConfig {
  enabled:        boolean
  channelId:      string | null
  filePrefix:     string
  horizonDays:    number
  dailyHour:      number
  resendOnChange: boolean
  ftpHost:        string | null
  ftpPort:        number
  ftpSecure:      boolean
  ftpUser:        string | null
  ftpDirectory:   string
  /** Le mot de passe n'est jamais renvoyé par l'API, seule sa présence l'est. */
  hasFtpPassword: boolean
  updatedAt:      string
}

/** `ftpPassword` absent = secret inchangé ; chaîne vide = secret effacé. */
export type EpgConfigPatch = Partial<Omit<EpgConfig, 'hasFtpPassword' | 'updatedAt'>>
  & { ftpPassword?: string }

export interface EpgDayStatus {
  dayKey:        string
  fileName:      string
  programCount:  number
  lastSentAt:    string | null
  lastStatus:    'SENT' | 'FAILED' | 'SKIPPED' | null
  lastError:     string | null
  /** La journée a changé depuis le dernier dépôt réussi. */
  pendingChange: boolean
}

export interface EpgExportOutcome {
  dayKey:       string
  fileName:     string
  status:       'SENT' | 'FAILED' | 'SKIPPED'
  programCount: number
  bytes:        number
  error?:       string
}

export interface EpgExportRecord {
  id:           string
  channelId:    string
  broadcastDay: string
  fileName:     string
  programCount: number
  bytes:        number
  status:       'SENT' | 'FAILED' | 'SKIPPED'
  trigger:      'DAILY' | 'CHANGE' | 'MANUAL'
  error:        string | null
  createdAt:    string
}

export const epgApi = {
  get:     ()                    => get<{ config: EpgConfig }>('/epg/config'),
  update:  (data: EpgConfigPatch) => put<{ config: EpgConfig }>('/epg/config', data),
  days:    ()                    => get<{ channelId: string | null; days: EpgDayStatus[] }>('/epg/days'),
  /** Dépôt manuel : toute la fenêtre si `day` est omis. */
  send:    (body: { day?: string; force?: boolean } = {}) =>
    post<{ channelId: string; outcomes: EpgExportOutcome[] }>('/epg/send', body),
  test:    ()                    => post<{ ok: boolean; message: string }>('/epg/test'),
  exports: (limit = 50)          => get<{ exports: EpgExportRecord[] }>(`/epg/exports${qs({ limit })}`),
  /**
   * Flux d'une journée tel qu'il partirait sur le FTP, en blob. Passe par
   * `httpBlob` et non par une URL ouverte dans un onglet : la route est en
   * ADMIN et le cookie de session est `SameSite`.
   */
  preview: (day: string)         => httpBlob(`/epg/preview${qs({ day })}`),
}

export const EPG_TRIGGER_LABELS: Record<string, string> = {
  DAILY:  'Dépôt quotidien',
  CHANGE: 'Modification de grille',
  MANUAL: 'Dépôt manuel',
}

export const EPG_STATUS_LABELS: Record<string, string> = {
  SENT:    'Transmis',
  FAILED:  'Échec',
  SKIPPED: 'Inchangé',
}

// ─── Direct YouTube (titre et description) ───────────────────────────────────

export interface YoutubeConfig {
  enabled:              boolean
  channelId:            string | null
  videoId:              string | null
  titleTemplate:        string
  idleTitle:            string
  descriptionFooter:    string
  titleLeadSec:         number
  oauthClientId:        string | null
  /** Le secret n'est jamais renvoyé par l'API, seule sa présence l'est. */
  hasOauthClientSecret: boolean
  connected:            boolean
  youtubeChannelTitle:  string | null
  lastSyncedAt:         string | null
  lastTitle:            string | null
  lastError:            string | null
  lastErrorAt:          string | null
  pausedUntil:          string | null
  pauseReason:          'QUOTA' | 'RETRY' | 'AUTH' | null
  updatedAt:            string
}

/** `oauthClientSecret` absent = secret inchangé ; chaîne vide = effacé. */
export type YoutubeConfigPatch = Partial<Pick<YoutubeConfig,
  'enabled' | 'videoId' | 'titleTemplate' | 'idleTitle' | 'descriptionFooter' | 'titleLeadSec' | 'oauthClientId'
>> & { oauthClientSecret?: string }

export interface YoutubePreview {
  title:       string
  description: string
  /** Identique au dernier envoi réussi. */
  upToDate:    boolean
  nextWakeAt:  string
}

export interface YoutubeDeviceCode {
  userCode:        string
  verificationUrl: string
  expiresInSec:    number
  intervalSec:     number
}

export type YoutubeAuthorizationStatus =
  | { status: 'PENDING' | 'DENIED' | 'EXPIRED' | 'NONE' }
  | { status: 'GRANTED'; config: YoutubeConfig }

export type YoutubeSyncOutcome =
  | { status: 'UPDATED'; title: string }
  | { status: 'UNCHANGED' }
  | { status: 'SKIPPED'; reason: 'DISABLED' | 'INCOMPLETE' | 'NO_CHANNEL' | 'PAUSED' | 'BUSY' }
  | { status: 'FAILED'; error: string }

export const youtubeApi = {
  get:                () => get<{ config: YoutubeConfig }>('/youtube/config'),
  update:             (data: YoutubeConfigPatch) => put<{ config: YoutubeConfig }>('/youtube/config', data),
  preview:            () => get<YoutubePreview>('/youtube/preview'),
  sync:               () => post<YoutubeSyncOutcome>('/youtube/sync'),
  authorize:          () => post<YoutubeDeviceCode>('/youtube/authorize'),
  pollAuthorization:  () => post<YoutubeAuthorizationStatus>('/youtube/authorize/poll'),
  disconnect:         () => post<{ config: YoutubeConfig }>('/youtube/disconnect'),
  currentDescription: () => get<{ description: string }>('/youtube/current-description'),
}
